/**
 * The backend reports required-field validation errors with the message template
 * `The {field} field is required.` and expects the frontend to substitute a human label
 * for the raw field name it sends alongside it (`requires_account`, `card_prefix`, …).
 */
export const FIELD_PLACEHOLDER = '{field}';

/**
 * Labels live under `form.field.<field>` — either in a module scope
 * (`subscriptions.form.field.requires_account`) or in the root/common bundle, which the
 * transloco loader merges into every language. Scoped keys win, the root bundle is the
 * fallback. A path (`prices[2].general_ledger_account_id`) falls back to its last segment's
 * label, and an unknown field degrades to its own name rather than a raw i18n key.
 */
export function resolveFieldLabel(field: string, translateFn: (key: string) => string, scopes: string[] = []): string {
  if (!field) {
    return '';
  }

  const leaf = field.includes('.') ? field.slice(field.lastIndexOf('.') + 1) : null;
  const names = leaf ? [field, leaf] : [field];
  const candidateKeys = names.flatMap((name) => [
    ...scopes.map((scope) => `${scope}.form.field.${name}`),
    `form.field.${name}`,
  ]);

  for (const key of candidateKeys) {
    const label = translateFn(key);
    // Transloco's default missing handler echoes the key back, so that means "not translated".
    if (label && label !== key) {
      return label;
    }
  }

  return humanizeFieldName(field);
}

/** `requires_account` -> `Requires account`. Last-resort label for a field with no translation. */
export function humanizeFieldName(field: string): string {
  const words = field.replace(/[._-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
